import { BadgeCheck, List, Package, Settings, Star, Store as StoreIcon } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { cn } from '@/lib/utils';

import { StoreImage, storeBannerUrl, storeLogoUrl } from '../storeMedia';
import { StoreRowMenu, type RequestStoreAction } from './StoreQuickActions';
import { StoreStatusBadge } from './StoreStatusBadge';
import { formatGmv, ownerLabel, planSummary, statusReason, storefrontPath } from './segments';

/**
 * Store roster as a card grid, mirroring the ff-ui Store Manager "Stores" tab
 * design (banner + overlapping logo pill + stats + footer) adapted to the admin
 * `AdminStore` row and admin navigation (cards open the moderation detail).
 */

const StoreCard: React.FC<{ store: AdminStore; onOpen: (id: number) => void; onAction: RequestStoreAction }> = ({
  store,
  onOpen,
  onAction,
}) => {
  const banner = storeBannerUrl(store);
  const listingCount = typeof store.listing_count === 'number' ? store.listing_count : null;
  const plan = planSummary(store);
  const reason = statusReason(store);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(store.storeid)}
      onKeyDown={(e) => {
        // Keys pressed inside the actions menu are not for the card.
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) onOpen(store.storeid);
      }}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-blue-200 hover:shadow-md"
    >
      {/* Banner */}
      <div className="relative h-28 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        <StoreImage src={banner} className="h-full w-full" iconClassName="h-24 w-24 text-gray-600 opacity-10" />
        <div className="absolute right-3 top-3 z-10 flex flex-wrap justify-end gap-1">
          {!!store.payout_hold && (
            <span className="inline-flex rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold leading-5 text-orange-800">Payouts held</span>
          )}
          <StoreStatusBadge store={store} />
        </div>
        {/* Logo pill */}
        <div className="absolute -bottom-6 left-5 z-10 flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-white shadow-md">
          <StoreImage src={storeLogoUrl(store)} alt={store.name} className="h-full w-full" iconClassName="h-7 w-7 text-gray-400" />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-1 px-5 pb-4 pt-8">
        <div className="flex items-center gap-1.5">
          <h3 className="truncate text-base font-semibold text-gray-900">{store.name}</h3>
          {store.verified === 1 && <BadgeCheck className="h-4 w-4 shrink-0 text-blue-500" aria-label="Verified" />}
          {store.featured === 1 && <Star className="h-4 w-4 shrink-0 text-purple-500" aria-label="Featured" />}
        </div>
        {(store.tagline || store.description_short) && (
          <p className="line-clamp-2 text-sm leading-snug text-gray-500">
            {store.tagline ?? store.description_short}
          </p>
        )}
        <p className="mt-0.5 truncate text-xs text-gray-400">
          {storefrontPath(store) ?? 'No handle'} · {ownerLabel(store)} · #{store.storeid}
        </p>
        <p className="truncate text-xs text-gray-500">
          <span className="font-medium text-gray-700">{plan.label}</span>
          {plan.detail && <span className={plan.expired ? 'text-red-600' : undefined}> · {plan.detail}</span>}
        </p>
        {reason && <p className="truncate text-xs text-red-700" title={reason}>{reason}</p>}

        {/* Stats */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-3">
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <List className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">{listingCount ?? '—'}</span>
            <span className="text-gray-400">
              {listingCount === 1 ? 'listing' : 'listings'}
              {listingCount ? ` · ${store.active_listing_count ?? 0} live` : ''}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-600" title={`${store.order_count ?? 0} orders`}>
            <Package className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">{store.order_count ?? '—'}</span>
            <span className="text-gray-400">orders · {formatGmv(store.gmv)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-3">
        <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors group-hover:border-blue-300 group-hover:text-blue-700">
          <Settings className="h-3.5 w-3.5" />
          Manage store
        </span>
        <StoreRowMenu store={store} onAction={onAction} />
      </div>
    </div>
  );
};

export const StoresGrid: React.FC<{
  rows: AdminStore[];
  loading: boolean;
  onOpen: (id: number) => void;
  onAction: RequestStoreAction;
}> = ({ rows, loading, onOpen, onAction }) => {
  if (loading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" aria-hidden="true">
            <div className="relative h-28 animate-pulse bg-gray-200">
              <div className="absolute right-3 top-3 h-5 w-14 rounded-full bg-gray-100" />
              <div className="absolute -bottom-6 left-5 h-14 w-14 rounded-xl border-2 border-white bg-gray-100 shadow-md" />
            </div>
            <div className="flex flex-1 flex-col gap-2 px-5 pb-4 pt-8">
              <div className="h-5 w-2/3 animate-pulse rounded bg-gray-200" />
              <div className="h-3.5 w-full animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
              <div className="mt-3 flex gap-4 border-t border-gray-100 pt-3">
                <div className="h-4 w-20 animate-pulse rounded bg-gray-100" />
                <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
            <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3">
              <div className="h-9 w-full animate-pulse rounded-lg bg-gray-100" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-12 text-center text-gray-500">
        <StoreIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
        <p className="text-sm font-medium">No stores match these filters</p>
        <p className="mt-1 text-xs">Adjust the search or clear a filter above.</p>
      </div>
    );
  }

  return (
    <div className={cn('grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ')}>
      {rows.map((store) => (
        <StoreCard key={store.storeid} store={store} onOpen={onOpen} onAction={onAction} />
      ))}
    </div>
  );
};
